import { useEffect, type ReactNode } from 'react';
import { useT } from '../i18n/locale-context';
import { LocaleLink } from '../i18n/locale-link';
import { InfoHint } from './info-hint';
import { track } from '../lib/funnel';
import { plansPathWithContext } from '../lib/pending-checkout';
import { paywallOncePerLot, usePaywallBlock } from '../lib/paywall-view';

/*
 * WHERE A LOCKED CARD SENDS THE READER — `plansPathWithContext('paywall', id)`.
 *
 * `?src=paywall` is read by `/plans` into `plans_viewed.source` — the value the
 * events registry has always described for a paywall CTA. The page's own Pro
 * links (`tender-gate-card`, `contact-reveal-card`, the source-link lock) go to
 * a BARE `/plans` and carry their attribution only on `upgrade_cta_clicked`; the
 * teasers do both, so the arrival is attributable even when the click event is
 * lost (an in-app browser killing the beacon, say).
 *
 * `?tender=<id>` is the same carrier every other paywall CTA on the tender page
 * already uses (`plansPathWithContext`, see `contact-reveal-card.tsx:70` and
 * `tender-detail.tsx:336`): `/plans` stashes it, the sign-in round trip and the
 * hosted provider carry it, and `/billing/return` spends it to put the buyer
 * back on the lot. A teaser that linked to a bare `/plans?src=paywall` would be
 * the one CTA on the page that forgets the tender — which is precisely the gap
 * the stash exists to close (75 of 98 new buyers had a lot open in the hour
 * before paying; 45 came back to it).
 *
 * `LocaleLink` prefixes the active locale and `localePath` keeps the query
 * verbatim, so uz gets `/uz/plans?src=paywall&tender=…`.
 */

/**
 * Locked-state teaser for a Pro-gated card (analytics, requirements, docs).
 *
 * The card renders as the REAL card with its values blurred out — same layout,
 * same tiles and bars, `children` supplying the body (see
 * `analytics-teaser-bodies.tsx`). The point is that a visitor can see WHAT
 * analytics exists and that it is populated for THIS tender, while being unable
 * to read the verdict. The predecessor — grey bars beside real labels — read as
 * «здесь пусто», which is the opposite of an upsell.
 *
 * Two things keep that from becoming a lie:
 *
 * - **No real value is in the page.** The server sends a locked viewer counts
 *   only (`comparableCount`, `contractCount`/`supplierCount`, `checksCount`) —
 *   never a median, share, HHI or price. The blurred figures are deliberate
 *   all-8 placeholders (`BlurredValue`), so un-blurring them in devtools yields
 *   nonsense rather than a number to act on.
 * - **One honest line per card.** `note` states what those counts say and
 *   nothing more: «По 500 сравнимым закупкам, …». It is the only sentence in
 *   the card that is true about this tender, and it is not blurred.
 *
 * Only render this when the server confirms data exists behind the wall, so it
 * is never a dead upsell on an empty tender.
 *
 * The WHOLE CARD is the affordance (2026-09-18): a stretched link over the
 * header and body navigates to `/plans?src=paywall` and fires
 * `upgrade_cta_clicked` with the wall's name as `source`. Still NO per-card
 * button with a label — the page keeps its single «Оформить Pro» CTA, and a Pro
 * button on every teaser is what this deliberately avoids. The lock glyph marks
 * the card as gated.
 *
 * Why a stretched link and not an `<a>` wrapped around everything: the header
 * carries an `InfoHint`, which is a `<button>`, and interactive content inside
 * a link is invalid HTML — a tap on the hint would navigate instead of opening
 * the tooltip. So the link is an absolutely-positioned sibling and the hint is
 * lifted above it. The `sr-only` «Доступно на Pro» sits outside the link too,
 * so a screen reader does not read it as the link's name.
 */
export function AnalyticsTeaser({
  title,
  hint,
  icon,
  rows,
  children,
  note,
  wall,
  tenderId,
}: {
  title: string;
  hint?: string;
  icon?: ReactNode;
  /** Fallback body: plain metric labels + decorative bars. Used by teasers with
   *  no real card to mirror (documents), and whenever `children` is absent. */
  rows?: string[];
  /** The mirrored card body — real layout, blurred values. */
  children?: ReactNode;
  /** The one true line, built from the server's non-decision counts. */
  note?: ReactNode;
  /** The gated feature this teaser fronts — emits a `paywall_shown` funnel
   *  event so we can see which wall converts. Optional (a teaser without it is
   *  simply not counted). */
  wall?: string;
  /**
   * The tender this teaser sits on, when it sits on one.
   *
   * WHICH LOT the wall was hit on was missing from almost every emitter: 256 of
   * 69,290 `paywall_shown` rows in the 30 days to 2026-09-18 carried a
   * `tenderId`, and all of them came from the contacts card. Without it the
   * wall cannot be joined to the lot the buyer actually wanted — the join that
   * showed 75 of 98 new buyers had a specific tender open in the hour before
   * paying. Omitted (not sent as null) off a tender page, e.g. on
   * `/supplier/:tin`, so the key's absence keeps meaning "not a tender wall".
   */
  tenderId?: string;
}) {
  const t = useT();
  // Spain (`analytics.paywallOncePerLot`): the card reports through the lot's single
  // wall hit — when it scrolls into view or is tapped — instead of on mount.
  const block = usePaywallBlock(wall, tenderId);

  useEffect(() => {
    if (paywallOncePerLot()) return;
    if (wall) track('paywall_shown', { wall, ...(tenderId ? { tenderId } : {}) });
  }, [wall, tenderId]);

  return (
    <section
      ref={block.ref}
      className="group relative overflow-hidden rounded-lg border border-edge bg-bone-surface shadow-m-sm transition-colors hover:border-brand/40"
    >
      {/* Covers header + body; `z-0` keeps it under the hint button, which is
          lifted to `z-10`. Focusable and keyboard-activatable, like any link. */}
      <LocaleLink
        to={plansPathWithContext('paywall', tenderId)}
        aria-label={t('teaser.openPlans', { card: title })}
        onClick={() => {
          block.onTap();
          if (wall) track('upgrade_cta_clicked', { source: wall });
        }}
        className="absolute inset-0 z-0 cursor-pointer rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
      />
      <div className="pointer-events-none relative flex items-center gap-3 p-5 sm:p-6">
        {icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-dim text-brand">
            {icon}
          </span>
        )}
        {/* The title WRAPS rather than being cut short: «Концентрация
            поставщиков заказчика» lost its last word in the 400 px sidebar and
            on a 390 px phone, and a card whose own name is clipped reads as
            broken, not as gated. Same header shape as the unlocked
            BuyerGraphPanel, which has always wrapped. */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <h3 className="text-[15px] font-bold text-ink">{title}</h3>
          {/* Above the stretched link, and clickable again — the hint is the one
              control inside the card that must NOT navigate. */}
          {hint && (
            <span className="pointer-events-auto relative z-10">
              <InfoHint text={hint} />
            </span>
          )}
        </div>
        {/* The 🔒 emoji, as everywhere else on the tender page (the gate card,
            the documents teaser, the source link). It was briefly swapped for an
            inline SVG after a screenshot review showed a tofu box — that was
            THIS host missing an emoji font, not a real device (Igor, 2026-09-18).
            `LockIcon` stays in `icons.tsx`, where /plans now imports it. */}
        <span aria-hidden data-teaser-lock="" className="shrink-0 text-[13px] text-ink-muted/70">
          🔒
        </span>
      </div>

      {/* Said once per card, not once per placeholder: every blurred figure is
          aria-hidden, so this is what a screen reader gets in their place. */}
      <span className="sr-only">{t('teaser.srLocked')}</span>

      {children ? (
        <div className="pointer-events-none relative">{children}</div>
      ) : (
        <div className="pointer-events-none relative flex flex-col gap-3 border-t border-edge-light px-5 py-4 sm:px-6">
          {(rows ?? []).map((label, i) => (
            <div key={i} className="flex items-center justify-between gap-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
                {label}
              </span>
              <span
                aria-hidden
                className="h-3 w-16 rounded bg-edge/70 blur-[1.5px]"
                style={{ width: `${44 + ((i * 17) % 40)}px` }}
              />
            </div>
          ))}
        </div>
      )}

      {note && (
        <p className="pointer-events-none relative border-t border-edge-light bg-bone px-5 py-3 text-[12px] leading-snug text-ink-muted sm:px-6">
          {note}
        </p>
      )}
    </section>
  );
}
